import React, { useState, useEffect, useRef } from 'react';
import humanizeStatusMessage from '../utils/humanizeStatus';
import StepLayout from '../components/StepLayout';
import ResponsivePaysMore from '../components/ResponsivePaysMore';
import ApproveOnPhone from '../components/ApproveOnPhone';

// B-FE-APP-APPROVAL-2 (2026-05-17): when V3 emits needs_app_approval
// during the login phase, replace the "Verifying credentials" spinner +
// status-line checklist with the polished ApproveOnPhone card (same
// component used by Step07TwoFA + Step18Finalizing). Kill-switch via
// REACT_APP_DISABLE_APP_APPROVAL_OVERLAY=1 reverts to the pre-fix
// behavior (spinner + checklist text). Pattern mirrors PR #357 + #356.
const APPROVAL_OVERLAY_DISABLED =
  process.env.REACT_APP_DISABLE_APP_APPROVAL_OVERLAY === '1';

// B-APPROVAL-RESOLVED (2026-05-17): clear overlay on the new resolved
// events V3 publishes between consecutive challenges. Setting this to
// '1' falls back to overlay-stays-until-terminal behavior.
const RESOLVED_EVENTS_DISABLED =
  process.env.REACT_APP_DISABLE_RESOLVED_EVENTS === '1';
// B-VERIFICATION-OVERLAY (2026-05-29): when the verification envelope is
// enabled (default), the /state GenericChallenge path owns the code/2FA
// surface. Step06 must NOT advance the wizard to the (now-retired) Step07
// on a code SSE event — it stays on the "verifying" spinner and the
// envelope replaces the screen via the /state projection within ~2.5s.
// Login dispatch is idempotent (SETNX login_v3 lock) so Step06 re-mounting
// after the challenge clears does not re-dispatch. Mirrors App.jsx's kill
// switch — set REACT_APP_DISABLE_VERIFICATION_ENVELOPE=1 to restore the
// legacy advance-to-Step07 behavior. Now imported from utils/featureFlags
// (single source of truth, shared with App.jsx) so the two definitions can't
// drift.
import { VERIFICATION_ENVELOPE_ENABLED } from '../utils/featureFlags';
import StepCaptchaViewer from '../components/StepCaptchaViewer';
import { api } from '../api';
import { connectSSE } from '../sse';

// FE-01 thresholds. Backstops, not primary progress signals.
// DISPATCH_CONFIRM_MS — max wait between `/login` acceptance and the
//   worker's step-4 log row appearing. Typical is <1s.
// STALL_MS — max duration for step 4 in 'started' state before we flag
//   the run as anomalous. Typical is 5–30s.
const DISPATCH_CONFIRM_MS = 30000;
const STALL_MS = 90000;
const POLL_INTERVAL_FAST_MS = 3000;
const POLL_INTERVAL_SLOW_MS = 10000;
const POLL_BACKOFF_AFTER_MS = 30000;
const RECOVERABLE_SESSION_STATUSES = new Set([
  'recovering',
  'waiting_for_input',
  'waiting_for_code',
  'waiting_for_2fa',
  'waiting_for_app_approval',
  'waiting_for_credentials_retry',
  // B-SELLER-RETRY (#523, 2026-06-02): the BE parks a login-leg seller-action
  // timeout (app-approval / captcha / agent-max-iter) here and App.jsx renders
  // SellerRetryScreen for it. Without this entry evaluateStatus() doesn't see
  // it as recoverable, falls through to the STALL_MS backstop, and can flash
  // "This is taking longer than expected" before the retry screen swaps in.
  'waiting_for_seller_retry',
  'operator_review',
]);
const TERMINAL_SESSION_STATUSES = new Set([
  'terminal_failed',
  'cancelled',
  'abandoned',
]);

export function isRecoverableSessionStatus(status) {
  return RECOVERABLE_SESSION_STATUSES.has(status);
}

export function isTerminalSessionStatus(status) {
  return TERMINAL_SESSION_STATUSES.has(status);
}

// PROXY-AUTO-REDISPATCH follow (2026-07-02) — exported pure helper for testing.
// On a proxy_check_fail the BE flips the session to a healthy proxy and AUTO
// re-dispatches the login as a fresh Celery task with a NEW taskId. The FE's
// taskId anchor (from its own /login dispatch) then goes stale, which would
// trip the "Login didn't start" terminator. This decides whether to adopt the
// newest login step-log's taskId as our anchor so we FOLLOW the BE's retry.
// Returns the new anchor { taskId, acceptedAt } to adopt, or null to keep the
// current one. Adopt only when: the session is still recoverable, the log has a
// taskId that DIFFERS from ours, and the log is genuinely NEWER than our anchor
// (guards against adopting a stale/older row).
export function reanchorLoginTarget(log, ctx, sessionStatus) {
  if (!log || !log.taskId || !ctx) return null;
  if (log.taskId === ctx.taskId) return null;
  if (!isRecoverableSessionStatus(sessionStatus)) return null;
  const started = Date.parse(log.startedAt);
  if (Number.isNaN(started) || started <= ctx.acceptedAt) return null;
  return { taskId: log.taskId, acceptedAt: started };
}

// Phase 2: map backend FailureType values to seller-facing copy. For
// INV-1 (profile/account mismatch) we explicitly avoid blaming the
// seller — the root cause is an ops pool defect, not bad credentials.
function resolveFailureCopy(log) {
  const ft = log?.failureType;
  if (ft === 'profile_account_mismatch') {
    return (
      "We can't continue with this account right now. Our support team "
      + "has been alerted and will follow up. You have not been charged "
      + "and your LinkedIn login was not changed."
    );
  }
  return log?.errorMessage || 'Login failed. Please try again.';
}

// B-CREDS-FE-EXPLICIT (2026-05-29): structured error kinds. We pass an
// object instead of a raw string so the error-render can branch on the
// canonical BE signal (status='waiting_for_credentials_retry' /
// terminal_reason='credential_retry_limit_exceeded') instead of regex-
// matching the message. The regex fallback remains for SSE-only paths
// and for legacy step_log error messages.
const ERROR_KIND = {
  WRONG_CREDENTIALS: 'wrong_credentials',
  CREDENTIAL_LIMIT_EXCEEDED: 'credential_limit_exceeded',
  GENERIC: 'generic',
};

export default function Step04AutomatedLogin({ step, totalSteps, sessionId, setSessionId, sessionData, updateData, onNext, goToStep, onRetryFromError }) {
  const [statusLines, setStatusLines] = useState([]);
  // iter-21: operator directive — never say "Logging in" / "Initializing" /
  // "Starting automated login". Use credential-verification framing instead.
  const [currentStatus, setCurrentStatus] = useState('Verifying credentials');
  // B-CREDS-FE-EXPLICIT (2026-05-29): error is now either '' (no error)
  // or an object { kind, message }. String-valued errors from legacy
  // callers are normalized to { kind: GENERIC, message: <string> } in
  // the render branch so we stay backwards-compatible.
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [captchaActive, setCaptchaActive] = useState(false);
  // B-FE-APP-APPROVAL-2 (2026-05-17): swap to the polished ApproveOnPhone
  // card when V3 emits needs_app_approval during login. Cleared on any
  // event that signals V3 moved past app-approval polling.
  const [showApproveOverlay, setShowApproveOverlay] = useState(false);
  const sseRef = useRef(null);
  const pollTimerRef = useRef(null);
  const startedRef = useRef(false);
  const taskContextRef = useRef(null); // { taskId, acceptedAt }
  const terminatedRef = useRef(false);
  // B-FE-GATE-3 (2026-05-16): set when V3 emits needs_app_approval via SSE.
  // While true, the STALL_MS anomaly backstop is suppressed entirely.
  // V3 legitimately polls LinkedIn for up to 300s for the post-approval
  // redirect, and the BE has no `awaiting_app_approval` flag on /status
  // (only `awaiting_2fa` for OTP/code flows), so this SSE-set ref is
  // the only positive signal we have that V3 is in approval-polling.
  const awaitingApprovalRef = useRef(false);
  // B-FE-GATE-4 (2026-05-16): timestamp of the last V3 SSE event of any
  // kind. STALL_MS measures silence since this timestamp instead of
  // since `log.startedAt`, so V3 doing legitimately slow work (LLM
  // calls 5-30s each, app-approval polling, captcha) doesn't trip
  // the backstop. Failure mode covered by da2406f3 + 71506b7f tests:
  // V3 emits needs_app_approval at ~95s after dispatch. The earlier
  // SSE-only bypass relied on the FE poll seeing awaitingApprovalRef
  // set BEFORE the 90s mark, but the poll/SSE race meant the poll
  // sometimes fired the stall trip before the SSE callback set the
  // ref. With activity tracking the stall clock resets on each
  // upstream V3 event, so the SSE race is no longer load-bearing.
  // Initialized in startLogin().
  const lastActivityRef = useRef(0);
  // B-CRED-QUEUE (2026-05-17, 2nd-opinion review #2 + #3): track queue
  // retry state across `startLogin()` re-entries so we can apply
  // exponential backoff + a wall-clock max-wait gate. Without these:
  // (a) fixed 5s retry from every queued seller = self-DDoS during
  //     ad-surge (cap=10 means 10 concurrent retries every 5s for an
  //     unbounded duration),
  // (b) no terminal condition means a queued seller sits indefinitely
  //     if the slot never clears (e.g., a wedged worker stranding the
  //     counter — the 20-min TTL on the BE side will eventually
  //     release it, but the FE shouldn't make the seller wait that
  //     long without an error path).
  const queueRetryRef = useRef({ firstAttemptAt: 0, attempts: 0 });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startLogin();
    return cleanup;
  }, []);

  const cleanup = () => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const terminate = (reason, payload) => {
    if (terminatedRef.current) return;
    terminatedRef.current = true;
    cleanup();
    if (reason === 'advance') {
      // Default: advance by 1 (Step 4 → Step 5, the 2FA prompt — used
      // when the backend emitted needs_code/2fa_required).
      setTimeout(() => onNext(), 500);
    } else if (reason === 'skip-2fa') {
      // V3 detected /feed/ already authenticated and returned done at
      // iter=1 — there is no 2FA challenge to enter. Jump directly to
      // Step 8 (Login Confirmation / "Is this you?"), bypassing Step 7
      // (TwoFA injection target) entirely. Step indices follow the v1
      // 22-step layout: this file is Step 6 (AutomatedLogin), Step 7 is
      // TwoFA, Step 8 is LoginConfirmation.
      // iter-26: skip TwoFA (step 10) → land on IDBadgeUpsell (step 11).
      // App.shouldSkip on step 11 will further auto-skip to step 12+ if
      // the seller doesn't qualify (no Passport ticked, already verified).
      setTimeout(() => goToStep && goToStep(11), 500);
    } else if (reason === 'error') {
      // B-CREDS-FE-EXPLICIT (2026-05-29): payload may be a string
      // (legacy) or { kind, message }. Normalize to the object shape
      // so the render branch can rely on it.
      if (payload && typeof payload === 'object' && payload.kind) {
        setError(payload);
      } else {
        setError({
          kind: ERROR_KIND.GENERIC,
          message: (typeof payload === 'string' && payload)
            || 'Login failed. Please try again.',
        });
      }
    }
  };

  const evaluateStatus = (statusBody) => {
    if (terminatedRef.current) return;
    const ctx = taskContextRef.current;
    if (!ctx) return; // haven't accepted a dispatch yet

    // B-VERIFICATION-OVERLAY (2026-05-29): while the BE is awaiting seller
    // input, treat it as proof-of-life so the STALL backstop never errors a
    // seller who is (or is about to be) on the verification overlay. The
    // /state envelope normally replaces this screen entirely within ~2.5s;
    // this is belt-and-suspenders for that window / a brief /state outage.
    if (
      VERIFICATION_ENVELOPE_ENABLED && statusBody && (
        statusBody.awaiting_2fa
        || statusBody.awaiting_email_change_2fa
        || statusBody.awaiting_app_approval
      )
    ) {
      lastActivityRef.current = Date.now();
    }
    const sessionStatus = statusBody?.status || null;

    if (isRecoverableSessionStatus(sessionStatus)) {
      return;
    }

    // B-CREDS-FE-EXPLICIT (2026-05-29): canonical wrong-credentials
    // signal from the BE state contract. Two cases both mean "your
    // email or password didn't work — verify and try again":
    //
    //   (a) status='waiting_for_credentials_retry' — recoverable; seller
    //       still has attempts left in the BE's per-cycle cap.
    //   (b) status='terminal_failed' AND terminalReason ===
    //       'credential_retry_limit_exceeded' — BE marked the session
    //       terminal because the cap was hit. We still show retry UI;
    //       the /credentials PUT route resets the session back to
    //       in_progress so the next /login dispatch can proceed.
    //
    // Both branches surface the BE-authored seller-safe message via
    // `statusBody.statusMessage` when present; otherwise fall back to
    // copy authored here. We pass the structured kind so the error
    // render can emphasize "verify your email + password before
    // retrying" instead of the generic Verification Failed copy.
    if (statusBody?.status === 'waiting_for_credentials_retry') {
      const msg = statusBody.statusMessage
        || "Those login details didn't work. Please check and try again.";
      // eslint-disable-next-line no-console
      console.log(
        '[B-CREDS-FE-EXPLICIT] waiting_for_credentials_retry — surfacing wrong-creds UI',
        { sessionId, kind: statusBody.lastRecoverableFailureKind, message: msg },
      );
      terminate('error', { kind: ERROR_KIND.WRONG_CREDENTIALS, message: msg });
      return;
    }
    if (
      statusBody?.status === 'terminal_failed'
      && statusBody?.terminalReason === 'credential_retry_limit_exceeded'
    ) {
      const msg = statusBody.statusMessage
        || "We couldn't verify those login details. "
           + 'Please double-check your LinkedIn email and password and try again.';
      // eslint-disable-next-line no-console
      console.log(
        '[B-CREDS-FE-EXPLICIT] terminal credential_retry_limit_exceeded — surfacing retry UI',
        { sessionId, message: msg },
      );
      terminate('error', { kind: ERROR_KIND.CREDENTIAL_LIMIT_EXCEEDED, message: msg });
      return;
    }

    // B-STEP06-RETRY-B26 (2026-05-17): session-level failure terminator.
    // The existing `matches && log.status==='failed'` path requires the
    // step_log's taskId to match the dispatch task. When V3 dies with
    // browser_closed_unexpectedly (B26 trap), the step_log IS marked
    // failed AND `takeover_sessions.status='failed'` is set via
    // mark_session_failed. But Filip Test 1 first attempt (2026-05-17)
    // showed the FE stuck on the spinner for the full 90s STALL_MS
    // window before recovering, because the session-status flip wasn't
    // observed by this hook.
    //
    // This branch terminates immediately only for structured terminal
    // statuses. Legacy `failed` is intentionally not terminal here:
    // /state is authoritative and will render either operator_review
    // or terminal_failed without exposing raw step-log failure text.
    // Kill switch REACT_APP_DISABLE_RETRY_ON_B26=1 falls back to
    // STALL_MS-only.
    if (
      process.env.REACT_APP_DISABLE_RETRY_ON_B26 !== '1'
      && isTerminalSessionStatus(sessionStatus)
    ) {
      const reason =
        statusBody.failureReason
        || 'Login failed unexpectedly. Please try again.';
      // eslint-disable-next-line no-console
      console.log(
        '[B-STEP06-RETRY-B26] session-failed terminator firing',
        { sessionId, status: statusBody.status, reason },
      );
      terminate('error', reason);
      return;
    }

    const log = statusBody?.latestLoginStepLog;
    const matches = !!(log && log.taskId && log.taskId === ctx.taskId);

    if (matches && log.stepNumber >= 5) {
      // Step >=5 means login is past the 2FA decision point. If the
      // step-log is for step 5 (2FA accepted code), or step 4 in
      // 'completed' status (no 2FA needed), advance normally to Step 5.
      // If status indicates the task ALREADY succeeded without going
      // through 2FA (step >= 6 already), jump past Step 5.
      if (statusBody?.currentStep && statusBody.currentStep >= 6) {
        terminate('skip-2fa');
      } else {
        terminate('advance');
      }
      return;
    }
    if (matches && log.stepNumber === 4 && log.status === 'completed') {
      // Login task finished clean (e.g. iter=1 done — already
      // authenticated). currentStep was bumped to 6. Jump past Step 5.
      terminate('skip-2fa');
      return;
    }
    if (matches && log.stepNumber === 4 && log.status === 'failed') {
      if (!isTerminalSessionStatus(sessionStatus)) {
        setCurrentStatus('Still working on your setup');
        setStatusLines((prev) => {
          const msg = "We're reconnecting and will continue automatically.";
          if (prev.length && prev[prev.length - 1] === msg) return prev;
          return [...prev, msg];
        });
        return;
      }
      terminate('error', resolveFailureCopy(log));
      return;
    }

    // PROXY-AUTO-REDISPATCH follow (2026-07-02): the BE flips to a healthy proxy
    // and AUTO re-dispatches login as a fresh task with a NEW taskId. Adopt that
    // newest login step-log's taskId (reanchorLoginTarget) so we FOLLOW the BE's
    // retry — and advance on its success — instead of tripping the "Login didn't
    // start" terminator below on the now-stale anchor. Resetting acceptedAt to
    // the new task's start also re-bases the dispatch-confirm / stall timers.
    if (!matches) {
      const reAnchor = reanchorLoginTarget(log, ctx, statusBody?.status);
      if (reAnchor) {
        taskContextRef.current = reAnchor;
        setCurrentStatus('Still working on your setup');
        return; // re-anchored — next poll matches the BE's redispatched task
      }
    }

    const now = Date.now();
    if (!matches && now - ctx.acceptedAt >= DISPATCH_CONFIRM_MS) {
      terminate('error', "Login didn't start. Please try again.");
      return;
    }
    if (matches && log.status === 'started') {
      // B-FE-GATE-3: suppress STALL_MS when V3 is in app-approval polling.
      // V3's own 300s timeout is the upper bound; on expiry the task
      // raises and the step-4 log flips to 'failed', which is handled
      // by the `log.status === 'failed'` branch above.
      if (awaitingApprovalRef.current) {
        return;
      }
      // B-FE-GATE-4: measure silence from the last V3 SSE event, NOT
      // from log.startedAt. V3 emits status/progress/agent_request/iter
      // events continuously while alive; the absence of ANY event for
      // STALL_MS is the true "stuck" signal. This makes the stall check
      // resilient to legitimately slow individual operations (multi-
      // second LLM calls, app-approval polling, etc.) that would
      // otherwise trip a startedAt-based timer.
      const logStartedMs = Date.parse(log.startedAt);
      const lastSignalMs = Math.max(
        Number.isNaN(logStartedMs) ? 0 : logStartedMs,
        lastActivityRef.current,
      );
      if (lastSignalMs > 0 && now - lastSignalMs >= STALL_MS) {
        // Before giving up with the generic stall message, do one
        // last status fetch — V3 might have just failed with a real
        // reason (e.g. "Wrong password") that our previous tick
        // missed. Surfacing that lets the seller know they need to
        // re-enter credentials, not just retry blindly.
        api.getStatus(sessionId).then((late) => {
          const lateLog = late?.latestLoginStepLog;
          if (isRecoverableSessionStatus(late?.status)) {
            setCurrentStatus('Still working on your setup');
          } else if (
            lateLog
            && lateLog.status === 'failed'
            && isTerminalSessionStatus(late?.status)
          ) {
            terminate('error', resolveFailureCopy(lateLog));
          } else {
            terminate('error', 'This is taking longer than expected. Please try again.');
          }
        }).catch(() => {
          terminate('error', 'This is taking longer than expected. Please try again.');
        });
        return;
      }
    }
  };

  const pollStatusOnce = async () => {
    if (terminatedRef.current) return;
    try {
      const body = await api.getStatus(sessionId);
      evaluateStatus(body);
    } catch (_err) {
      // Transient poll error — retry on next tick. Do NOT trip the
      // dispatch-confirm timer based on network blips.
    }
    schedulePoll();
  };

  const schedulePoll = () => {
    if (terminatedRef.current) return;
    const ctx = taskContextRef.current;
    const elapsed = ctx ? Date.now() - ctx.acceptedAt : 0;
    const interval = elapsed >= POLL_BACKOFF_AFTER_MS
      ? POLL_INTERVAL_SLOW_MS
      : POLL_INTERVAL_FAST_MS;
    pollTimerRef.current = setTimeout(pollStatusOnce, interval);
  };

  const startLogin = async () => {
    setError('');
    setStatusLines([]);
    setCurrentStatus('Verifying credentials');
    terminatedRef.current = false;
    // B-FE-GATE-4: prime the activity clock so the stall check has a
    // baseline before any SSE event arrives. Without this, lastSignalMs
    // would fall back to log.startedAt (the old behaviour) until the
    // first event lands, leaving a window for the original race.
    lastActivityRef.current = Date.now();

    // Open SSE first — low-latency path for happy flow (2fa_required)
    // and for 'error' events emitted by the task's except handler.
    sseRef.current = connectSSE(sessionId, (event) => {
      if (terminatedRef.current) return;
      // B-FE-GATE-4: ANY event from V3 is proof of life. Reset the
      // activity clock before branch-handling so the stall check
      // measures silence since the last event, not since startedAt.
      lastActivityRef.current = Date.now();
      if (event.type === 'status' || event.type === 'progress') {
        const rawMessage = event.message || event.status
          || (event.action ? `${event.action}: ${event.why || ''}` : 'Processing...');
        // iter-17: collapse raw V3 traces into seller-friendly copy.
        // Returns null for events the seller doesn't need to see (raw
        // fill/click/navigate, internal milestones, selectors).
        const friendly = humanizeStatusMessage(rawMessage);
        if (friendly !== null) {
          setCurrentStatus(friendly);
          setStatusLines((prev) => {
            // De-dupe consecutive identical messages (V3 emits a flurry
            // of similar progress events when navigating multi-page
            // flows; the seller doesn't need to see "Working on it..."
            // 12 times in a row).
            if (prev.length && prev[prev.length - 1] === friendly) return prev;
            return [...prev, friendly];
          });
        }
      } else if (event.type === 'agent_request') {
        // iter-18: operator directive — never mention "agent" in
        // seller-facing copy. iter-21: also never say "Logging in" — use
        // eligibility framing instead while V3's LLM call is in flight.
        setCurrentStatus('Verifying eligibility');
      } else if (event.type === 'code_received') {
        setStatusLines((prev) => [...prev, 'Code accepted']);
      } else if (event.type === 'completed' || event.type === 'login_success') {
        // iter-21: avoid "Login successful" framing per operator directive.
        setCurrentStatus('Verification complete');
        setStatusLines((prev) => [...prev, 'Verification complete']);
        // Login finished without ever asking for a code — skip Step 5.
        // CRITICAL: only advance on login_success, NOT v3_done. v3_done
        // fires before the post-V3 scrape commits scraped_name; jumping
        // to Step 6 then would land on the "Jane Doe" stub. login_success
        // is published after scrape persistence.
        terminate('skip-2fa');
      } else if (event.type === 'v3_done') {
        // Status update only — scrape hasn't finished yet. login_success
        // (or needs_code, if 2FA is required) will fire next.
        setCurrentStatus('Finalising profile data...');
      } else if (event.type === 'account_restricted') {
        // V3 detected a hard LinkedIn restriction surface (phone
        // verification entry form, government-ID upload, "account on
        // hold" page) that the operator CANNOT resolve on the seller's
        // behalf. Surface a clear "account unavailable" message rather
        // than a generic "login failed" — sellers like Bifored Jezab
        // (2026-05-14) saw "automation error" and DM'd the operator
        // confused; this UX gives them the right answer.
        const ctx = event.context ? ` (${event.context})` : '';
        terminate(
          'error',
          'This LinkedIn account is not available. LinkedIn has flagged it ' +
          'with a security check we cannot complete on your behalf' + ctx + '. ' +
          'Please use a different LinkedIn account, or contact support if you believe ' +
          'this is an error.',
        );
      } else if (event.type === 'error' || event.type === 'login_failed' || event.type === 'v3_blocked') {
        const msg = event.reason || event.message || event.error
          || 'Login failed. Please try again.';
        // B-CREDS-FE-EXPLICIT (2026-05-29): SSE arrives slightly before
        // the BE persists status='waiting_for_credentials_retry', so the
        // /status poll's canonical branch may not have fired yet. Match
        // the BE classifier's markers (see session_state.py
        // _CREDENTIAL_RETRY_MARKERS) on the event reason so the seller
        // still sees the wrong-credentials UI from the SSE path. Same
        // regex tokens, just promoted to a structured kind.
        if (/wrong email or password|invalid_credentials|credentials rejected/i.test(msg)) {
          terminate('error', {
            kind: ERROR_KIND.WRONG_CREDENTIALS,
            message: msg,
          });
          return;
        }
        // Otherwise defer to the canonical session state (session-state
        // contract, master): recoverable statuses keep the seller
        // waiting rather than terminating; everything else terminates
        // with the BE's seller-safe message.
        const safeFallback = 'We could not complete verification automatically. Please try again or contact support.';
        api.getSessionState(sessionId).then((state) => {
          if (isRecoverableSessionStatus(state?.status)) {
            setCurrentStatus('Still working on your setup');
            setStatusLines((prev) => {
              const line = state?.status_message_safe_for_seller
                || "We're reconnecting and will continue automatically.";
              if (prev.length && prev[prev.length - 1] === line) return prev;
              return [...prev, line];
            });
            return;
          }
          terminate('error', state?.status_message_safe_for_seller || safeFallback);
        }).catch(() => {
          terminate('error', safeFallback);
        });
      } else if (
        event.type === '2fa_required'
        || event.type === 'needs_code'
        || event.type === 'needs_otp'
      ) {
        // 2FA v2: V3 emits the variant-specific reason; legacy publishes
        // '2fa_required' / 'needs_code'. Both default to OTP rendering
        // on Step 5 — the OTP CodeEntryPanel covers authenticator-app
        // and SMS cases, which is what the legacy reasons most often
        // signaled.
        setCurrentStatus('Two-factor authentication required');
        setStatusLines((prev) => [...prev, 'Two-factor authentication required']);
        if (updateData) updateData({ twoFAMode: 'otp' });
        // B-VERIFICATION-OVERLAY: envelope ON → stay on the spinner; the
        // /state projection flips to waiting_for_2fa/code and App replaces
        // this screen with GenericChallenge. Only the legacy path advances.
        if (!VERIFICATION_ENVELOPE_ENABLED) terminate('advance');
      } else if (event.type === 'needs_email_code') {
        setCurrentStatus('Email verification code required');
        setStatusLines((prev) => [...prev, 'LinkedIn sent a code to your email']);
        if (updateData) updateData({ twoFAMode: 'email_code' });
        if (!VERIFICATION_ENVELOPE_ENABLED) terminate('advance');
      } else if (event.type === 'needs_app_approval') {
        // B-FE-GATE-1 (2026-05-16): stay on this page while V3 polls
        // LinkedIn for the post-approval redirect. Previously we called
        // terminate('advance'), but App.next() skips Step07TwoFA (always
        // skipped per shouldSkip rule) and the seller landed on
        // Step09IDBadgeUpsell while login was still polling — Filip's
        // 2026-05-16 live test showed this advanced the seller all the
        // way to Step18Finalising while V3 was hung at iter=5 on
        // needs_app_approval. V3 owns the wait loop (300s); on success
        // it emits login_success which our existing handler catches,
        // on timeout it emits error/v3_blocked which we also handle.
        //
        // B-FE-GATE-3 (2026-05-16): set awaitingApprovalRef so the
        // STALL_MS=90s anomaly backstop is suppressed for the duration
        // of V3's polling. Without this, the second live test of the
        // day (session da2406f3) saw the FE correctly stay on this
        // page (B-FE-GATE-1 working) but then surface "Verification
        // Failed — This is taking longer than expected" at the 90s
        // mark while V3 was still healthy in its 300s polling window.
        awaitingApprovalRef.current = true;
        setCurrentStatus('Approve sign-in on your phone');
        setStatusLines((prev) => {
          const msg = 'Open the LinkedIn app on your phone and tap "Yes" to approve';
          if (prev.length && prev[prev.length - 1] === msg) return prev;
          return [...prev, msg];
        });
        if (updateData) updateData({ twoFAMode: 'app_approval' });
        // B-FE-APP-APPROVAL-2: render the polished card instead of the
        // spinner-with-checklist. Kept inside the same branch so the
        // legacy statusLines fallback still updates for kill-switch
        // and for the moment between SSE and overlay render.
        if (!APPROVAL_OVERLAY_DISABLED) {
          // eslint-disable-next-line no-console
          console.log(
            '[B-FE-APPROVAL-2] showing ApproveOnPhone overlay, phase=login',
            { sessionId, eventType: event.type },
          );
          setShowApproveOverlay(true);
        }
      } else if (
        !RESOLVED_EVENTS_DISABLED
        && (event.type === 'app_approval_resolved'
            || event.type === '2fa_resolved')
      ) {
        // B-APPROVAL-RESOLVED (2026-05-17): clear overlay so the next
        // needs_app_approval can re-show it cleanly. login_success
        // still arrives later as the terminal advance signal.
        // eslint-disable-next-line no-console
        console.log(
          '[B-APPROVAL-RESOLVED] clearing overlay, phase=login',
          { sessionId, eventType: event.type },
        );
        awaitingApprovalRef.current = false;
        setShowApproveOverlay(false);
      } else if (event.type === 'needs_captcha') {
        // V3 detected a captcha (Layer A). Mount the cropped-noVNC
        // overlay so the seller can solve the puzzle directly.
        // V3 polls for clearance and will publish captcha_solved or
        // captcha_failed when done.
        setCurrentStatus('Security verification required');
        setStatusLines((prev) => [...prev, 'Security challenge. Please complete below']);
        setCaptchaActive(true);
      } else if (event.type === 'captcha_solved') {
        setCaptchaActive(false);
        setCurrentStatus('Verification complete, continuing login...');
        setStatusLines((prev) => [...prev, 'Security verification complete']);
      } else if (event.type === 'captcha_failed') {
        setCaptchaActive(false);
        const reason = event.reason || 'Verification timed out';
        terminate('error', `Verification failed: ${reason}`);
      }
    });

    // Dispatch the task. Response carries the authoritative taskId for
    // correlation; we poll /status and trust only step-log rows whose
    // metadata.task_id equals this value.
    try {
      const resp = await api.startLogin(sessionId);
      // B-CRED-QUEUE (2026-05-17): BE returns started=false +
      // queuePosition when the DICloak host's concurrent-login cap
      // is exhausted. Render a "you're Nth in line" message and
      // retry /login with EXPONENTIAL BACKOFF (5s → 10s → cap 20s),
      // bounded by a 60s wall-clock max-wait AND 12-attempt count.
      // Kill switch REACT_APP_DISABLE_CRED_QUEUE=1 falls back to
      // immediate-error UX (resp.queued would still flow but we'd
      // ignore it).
      //
      // Per 2nd-opinion review items #2 + #3:
      //   - Fixed 5s retry from every queued seller = self-DDoS during
      //     ad-surge. Exp-backoff smooths the herd.
      //   - Without a max-wait gate, a wedged worker leaves seller
      //     sitting indefinitely (20-min TTL eventually clears but
      //     too long for an FE error path).
      // B-PRECHECK-EARLY (2026-05-19, PR #409): BE gates V3 dispatch
      // on `assigned_email_id IS NOT NULL`. If the buyer-email precheck
      // (now dispatched at session-create, not /credentials) hasn't yet
      // claimed an email, /login returns `preparing=true` and we retry
      // on the SAME backoff schedule as the credentials queue. The
      // precheck normally finishes 30-60s before the seller submits
      // credentials, so this branch is rarely hit — but when it is,
      // the seller sees the safe "Preparing your account" message
      // instead of a stale spinner or terminal failure.
      if (resp?.preparing) {
        const now = Date.now();
        if (queueRetryRef.current.firstAttemptAt === 0) {
          queueRetryRef.current.firstAttemptAt = now;
        }
        queueRetryRef.current.attempts += 1;
        const elapsedMs = now - queueRetryRef.current.firstAttemptAt;
        const MAX_WAIT_MS = 360000;
        const MAX_ATTEMPTS = 60;
        if (elapsedMs >= MAX_WAIT_MS || queueRetryRef.current.attempts >= MAX_ATTEMPTS) {
          // eslint-disable-next-line no-console
          console.log(
            '[B-PRECHECK-EARLY] preparing_timeout — giving up',
            { sessionId, elapsedMs, attempts: queueRetryRef.current.attempts },
          );
          terminate(
            'error',
            "We're still preparing your account. Please try again in a few minutes.",
          );
          return;
        }
        setCurrentStatus(
          resp.preparingMessage || 'Preparing your account. This can take a moment.',
        );
        const backoffMs = Math.min(
          10000,
          3000 * Math.pow(1.5, queueRetryRef.current.attempts - 1),
        );
        // eslint-disable-next-line no-console
        console.log(
          '[B-PRECHECK-EARLY] preparing, retrying',
          {
            sessionId,
            backoffMs,
            attempts: queueRetryRef.current.attempts,
            elapsedMs,
          },
        );
        setTimeout(() => {
          if (!terminatedRef.current) startLogin();
        }, backoffMs);
        return;
      }
      const queueDisabled =
        process.env.REACT_APP_DISABLE_CRED_QUEUE === '1';
      if (resp?.queued && !queueDisabled) {
        const now = Date.now();
        if (queueRetryRef.current.firstAttemptAt === 0) {
          queueRetryRef.current.firstAttemptAt = now;
        }
        queueRetryRef.current.attempts += 1;
        const elapsedMs = now - queueRetryRef.current.firstAttemptAt;
        const MAX_WAIT_MS = 60000;
        const MAX_ATTEMPTS = 12;
        if (elapsedMs >= MAX_WAIT_MS || queueRetryRef.current.attempts >= MAX_ATTEMPTS) {
          // eslint-disable-next-line no-console
          console.log(
            '[B-CRED-QUEUE] queue_timeout — giving up',
            { sessionId, elapsedMs, attempts: queueRetryRef.current.attempts },
          );
          terminate(
            'error',
            "We're getting a lot of traffic right now. Please try again in a few minutes.",
          );
          return;
        }
        const pos = resp.queuePosition || 1;
        const ordinal =
          pos === 1 ? '1st' : pos === 2 ? '2nd' : pos === 3 ? '3rd' : `${pos}th`;
        setCurrentStatus(
          `You're ${ordinal} in line, waiting for an available slot...`,
        );
        setStatusLines((prev) => {
          const msg = `Server is busy, ${ordinal} in line`;
          if (prev.length && prev[prev.length - 1] === msg) return prev;
          return [...prev, msg];
        });
        // Exp-backoff capped at 20s. attempts=1→5s, 2→10s, 3+→20s.
        const backoffMs = Math.min(
          20000,
          5000 * Math.pow(2, queueRetryRef.current.attempts - 1),
        );
        // eslint-disable-next-line no-console
        console.log(
          '[B-CRED-QUEUE] queued, retrying with exp-backoff',
          {
            sessionId,
            position: pos,
            cap: resp.queueCap,
            current: resp.queueCurrent,
            backoffMs,
            attempts: queueRetryRef.current.attempts,
            elapsedMs,
          },
        );
        setTimeout(() => {
          if (!terminatedRef.current) startLogin();
        }, backoffMs);
        return;
      }
      // Cleared queue (got a slot) — reset retry state so any future
      // re-queue (e.g., recovery dispatch) starts fresh.
      queueRetryRef.current = { firstAttemptAt: 0, attempts: 0 };
      taskContextRef.current = {
        taskId: resp?.taskId || null,
        acceptedAt: Date.now(),
      };
      if (!taskContextRef.current.taskId) {
        // Backend didn't return a taskId — we can still advance via SSE
        // but can't do backstop polling safely. Fall back to SSE-only.
        return;
      }
      schedulePoll();
    } catch (err) {
      terminate('error', err.message || 'Failed to start login process.');
    }
  };

  const handleRetry = async () => {
    // iter-19: operator directive — Try Again must take the seller back
    // to the Credentials step so they can re-enter email/password.
    // Re-running startLogin() with the same (likely-bad) creds just
    // re-fails. onRetryFromError navigates to Step06 LinkedIn Credentials.
    setRetrying(true);
    startedRef.current = false;
    terminatedRef.current = false;
    taskContextRef.current = null;
    cleanup();
    if (typeof onRetryFromError === 'function') {
      onRetryFromError();
    } else {
      // Fallback for the (no longer used) in-place retry: re-run login.
      await startLogin();
    }
    setRetrying(false);
  };

  const spinnerStyle = {
    // Operator walk 2026-05-09: ring inverted from var(--border) (black) to
    // white so the orange highlight is the dominant visual. Static ring
    // sits on the warm-off-white card and reads as a faint outline; the
    // spinning quarter-arc is var(--primary) orange.
    width: 48,
    height: 48,
    border: '4px solid #ffffff',
    borderTopColor: 'var(--primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  };

  const statusLineStyle = {
    color: 'var(--text2)',
    fontSize: 13,
    padding: '4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const checkStyle = {
    color: 'var(--success, #22c55e)',
    fontSize: 14,
    flexShrink: 0,
  };

  // B-FE-APP-APPROVAL-2 (2026-05-17): swap to the polished card when
  // V3 is polling LinkedIn for the seller's phone tap during login.
  // This branches BEFORE the spinner-with-checklist render so the
  // seller gets unambiguous "tap Approve on your phone" UX instead of
  // a wall of status text. Captcha takes priority (caller needs to
  // solve the puzzle inline before approval can fire).
  if (showApproveOverlay && !captchaActive && !error) {
    return (
      <StepLayout title="Approve sign-in on your phone" variant="centered">
        <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 20, textAlign: 'center' }}>
          LinkedIn requires you to confirm this sign-in.
        </p>
        <ApproveOnPhone
          subtitle="We'll continue automatically once LinkedIn confirms your approval."
        />
      </StepLayout>
    );
  }

  // B-CREDS-FE (2026-05-30): when the verification envelope is on, the App.jsx
  // wrong-credentials overlay (#485) owns the wrong-creds surface (with the
  // in-app Reset password CTA). Step06 must NOT render its own older escalation
  // ("Still not working. Try resetting your LinkedIn password" + the external
  // reset link) — it flashed in the ~3s before the overlay covers it and on the
  // 2nd+ wrong-password attempt, and conflicts with the overlay's in-app reset.
  // Defer: stay on the neutral spinner; the overlay drives. (Mirrors how #490
  // made Step06 defer challenges to the envelope.)
  const deferWrongCredsToOverlay =
    VERIFICATION_ENVELOPE_ENABLED
    && error && typeof error === 'object'
    && (error.kind === ERROR_KIND.WRONG_CREDENTIALS
        || error.kind === ERROR_KIND.CREDENTIAL_LIMIT_EXCEEDED);

  return (
    <StepLayout
      title={captchaActive ? null : 'Verifying credentials'}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {captchaActive ? (
        <StepCaptchaViewer
          sessionId={sessionId}
          onError={(reason) => {
            setCaptchaActive(false);
            terminate('error', `Verification could not be loaded: ${reason}`);
          }}
        />
      ) : (!error || deferWrongCredsToOverlay) ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={spinnerStyle} />
          <p style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            {currentStatus}
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 4 }}>
            We're signing in to LinkedIn for you. This usually takes 20-60 seconds.
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 4 }}>
            If LinkedIn asks for a verification code or app approval, this page will update automatically. Don't refresh.
          </p>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 24, fontWeight: 600 }}>
            Please keep this tab open.
          </p>

          <ResponsivePaysMore placement="automatedLogin" sessionData={sessionData} />

          {statusLines.length > 0 && (
            <div style={{
              background: 'var(--surface)',
              borderRadius: 8,
              padding: 16,
              textAlign: 'left',
            }}>
              {statusLines.map((line, i) => (
                <div key={i} style={statusLineStyle}>
                  <span style={checkStyle}>&#10003;</span>
                  <span>{line}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: 24,
            color: 'var(--danger, #ef4444)',
          }}>
            !
          </div>
          {(() => {
            // B-CREDS-FE-EXPLICIT (2026-05-29): error is now either a
            // legacy string or a structured { kind, message }. Normalize
            // here so the rest of the branch can read both shapes
            // uniformly. The legacy regex-match path still runs as a
            // fallback for paths that pass a raw string (e.g.,
            // resolveFailureCopy from a step_log row that we can't yet
            // tie back to a canonical BE state).
            const errObj = (error && typeof error === 'object' && error.kind)
              ? error
              : null;
            const errMessage = errObj?.message
              ?? (typeof error === 'string' ? error : '');
            // Explicit BE-signal wrong-creds (highest confidence) OR
            // legacy regex match on the raw message (fallback).
            const looksLikeWrongCreds =
              errObj?.kind === ERROR_KIND.WRONG_CREDENTIALS
              || errObj?.kind === ERROR_KIND.CREDENTIAL_LIMIT_EXCEEDED
              || /wrong (?:email|password)|incorrect credentials|credentials.*rejected|wrong.*credentials/i.test(errMessage);
            const explicitCredLimit =
              errObj?.kind === ERROR_KIND.CREDENTIAL_LIMIT_EXCEEDED;
            // B34 (2026-05-15): track wrong-creds count per session in
            // localStorage so we can escalate the copy after repeated
            // rejections. Nusu Pobefep went 6 attempts tonight typing
            // the wrong password each time — the second+ rejection
            // should surface "you might want to reset your password"
            // rather than just say "try again".
            let wrongCount = 0;
            if (looksLikeWrongCreds && typeof window !== 'undefined') {
              try {
                const key = `takeover:wrongpw:${sessionId}`;
                wrongCount = parseInt(window.localStorage.getItem(key) || '0', 10) + 1;
                window.localStorage.setItem(key, String(wrongCount));
              } catch { /* localStorage disabled */ }
            }
            // The BE cap also counts as escalation — by the time the BE
            // declares credential_retry_limit_exceeded the seller has
            // already tried 3 times.
            const escalated = looksLikeWrongCreds && (wrongCount >= 2 || explicitCredLimit);
            const headline = looksLikeWrongCreds
              ? (escalated
                  ? (explicitCredLimit
                      ? 'Email or password is incorrect'
                      : 'Still not working. Try resetting your LinkedIn password')
                  : 'Email or password is incorrect')
              : 'Verification Failed';
            const body = explicitCredLimit
              ? "We weren't able to sign in with the email and password you entered. "
                + 'Please double-check both. Make sure your email has no typos '
                + "(e.g. .com not .con) and that your password is correct, then try again."
              : escalated
                ? 'LinkedIn rejected your credentials multiple times. The fastest way to recover is to reset your LinkedIn password (link below), then come back here and enter the new password.'
                : looksLikeWrongCreds
                  ? 'LinkedIn rejected the email or password you entered. Please verify both your LinkedIn email and password are correct, then try again.'
                  : (errMessage || 'Login failed. Please try again.');
            const buttonText = retrying
              ? 'Retrying...'
              : (looksLikeWrongCreds ? 'Re-enter credentials' : 'Try Again');
            return (
              <>
                <p style={{ color: 'var(--danger, #ef4444)', fontSize: 15, marginBottom: 8, fontWeight: 600 }}>
                  {headline}
                </p>
                <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: escalated ? 12 : 24 }}>
                  {body}
                </p>
                {escalated && (
                  <a
                    href="https://www.linkedin.com/uas/request-password-reset"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      color: 'var(--primary)',
                      fontSize: 14,
                      fontWeight: 600,
                      textDecoration: 'underline',
                      marginBottom: 18,
                    }}
                  >
                    Reset my LinkedIn password →
                  </a>
                )}
                <button
                  onClick={handleRetry}
                  disabled={retrying}
                  style={{
                    background: retrying ? 'var(--primary-soft)' : 'var(--primary)',
                    color: '#fff',
                    padding: 14,
                    borderRadius: 8,
                    border: 'none',
                    fontSize: 16,
                    fontWeight: 600,
                    width: '100%',
                    cursor: retrying ? 'not-allowed' : 'pointer',
                    opacity: retrying ? 0.7 : 1,
                  }}
                >
                  {buttonText}
                </button>
              </>
            );
          })()}
        </div>
      )}
    </StepLayout>
  );
}
